// ============================================================================
// Beacon — AI offer / negotiation letter (Claude). Reads the negotiation
// worksheet (revised number + itemized findings) and writes a firm, professional,
// data-backed letter to the seller / bank / agent. Same split-for-security
// pattern as the analyzer: the PROMPT is built here (pure, tested), the API key
// lives in the `analyze` Edge Function (reused). The buyer's internal return
// math never goes into the letter — the case is made from documented findings.
// ============================================================================

import type { Deal } from "../types";
import { buildNegotiation, type Negotiation } from "./negotiation";
import { withDiligence, negotiableDefects } from "./diligence";
import { money0 } from "./format";
import { isCloud, getSupabase } from "./supabase";
import { logUsage } from "./usage";

export type Recipient = "seller" | "bank" | "agent" | "wholesaler";

export const RECIPIENTS: { id: Recipient; label: string; blurb: string }[] = [
  { id: "seller", label: "Seller (owner)", blurb: "Respectful but firm; certainty & speed of close." },
  { id: "bank", label: "Bank / REO", blurb: "All business; numbers and net cost-to-cure first." },
  { id: "agent", label: "Listing agent", blurb: "Professional; ammunition to present to their client." },
  { id: "wholesaler", label: "Wholesaler", blurb: "Direct; the number and a fast, clean close." },
];

export const OFFER_LETTER_SYSTEM =
  "You are drafting a real-estate purchase-offer / price-revision letter on behalf of a professional " +
  "investor-buyer. The buyer has completed due diligence and is revising their offer downward based on " +
  "documented property conditions. Write a persuasive, firm, and professional letter that makes the case " +
  "ENTIRELY from the documented findings and their repair costs — plus general market/condition and " +
  "project-risk reasoning. CRITICAL RULES: (1) Never disclose the buyer's internal return targets, cap-rate " +
  "math, or profit ceiling — those are confidential; justify the number by the findings, the cost to cure, " +
  "and the risk/holding cost of the project. (2) Use only facts provided; do not invent findings or numbers. " +
  "(3) Be respectful and business-like, not adversarial — the goal is a deal that closes. (4) Reference that " +
  "supporting documentation (inspection findings, photos, reports) is attached. (5) Lead with a brief, warm " +
  "opening, then the findings and their cost, then the revised offer clearly stated, then a confident close " +
  "with next steps and the buyer's strengths (certainty, speed, as-is close). Adjust tone to the recipient. " +
  "Output only the letter text, ready to send; keep it tight (roughly 250–400 words).";

export interface OfferLetterInputs {
  recipient: Recipient;
  senderName?: string;
  senderEntity?: string;
  recipientName?: string;
  closeDays?: number; // e.g. 21-day close as a strength
  note?: string; // any extra context from the user
}

/** Pure: build the user prompt for the offer letter from the deal + worksheet. */
export function buildOfferLetterPrompt(deal: Deal, n: Negotiation, inp: OfferLetterInputs): string {
  const defects = negotiableDefects(withDiligence(deal));
  const findingLines = n.reductions.length
    ? n.reductions
        .map((r) => `- ${r.label}${r.severity ? ` (${r.severity})` : ""}: est. ${money0(r.amount)} to cure${r.source ? ` [${r.source}]` : ""}`)
        .join("\n")
    : "- (No itemized defects recorded; base the revision on market/condition and project risk.)";
  const safety = defects.filter((d) => d.severity === "safety").map((d) => `${d.system}: ${d.label}`);

  const toneMap: Record<Recipient, string> = {
    seller: "Recipient is the property OWNER/seller — be respectful of their situation, but firm; emphasize a certain, fast, as-is close.",
    bank: "Recipient is a BANK / REO asset manager — all business; lead with the numbers and the net cost to cure; unemotional.",
    agent: "Recipient is the LISTING AGENT — professional and collegial; give them clean talking points to bring to their client.",
    wholesaler: "Recipient is a WHOLESALER — direct and brief; the number, proof of funds implied, and a fast clean close.",
  };

  return [
    `PROPERTY: ${deal.name}${deal.addr ? ` — ${deal.addr}` : ""} (${deal.market}).`,
    `ASKING / CURRENT PRICE: ${money0(n.asking)}.`,
    `REVISED OFFER: ${money0(n.revisedOffer)} (a ${Math.round(n.discount * 100)}% reduction of ${money0(n.asking - n.revisedOffer)}).`,
    `TOTAL DOCUMENTED REPAIR/CLEARANCE COST: ${money0(n.totalReduction)}.`,
    ``,
    `DOCUMENTED FINDINGS (the basis for the revision):`,
    findingLines,
    safety.length ? `\nSAFETY-CRITICAL ITEMS (emphasize): ${safety.join("; ")}.` : ``,
    ``,
    `RECIPIENT: ${toneMap[inp.recipient]}`,
    inp.recipientName ? `Address the letter to: ${inp.recipientName}.` : ``,
    inp.senderName ? `From: ${inp.senderName}${inp.senderEntity ? `, ${inp.senderEntity}` : ""}.` : `From: the buyer${inp.senderEntity ? ` (${inp.senderEntity})` : ""}.`,
    inp.closeDays ? `Buyer strength: can close in ${inp.closeDays} days, as-is, no financing contingency.` : `Buyer strength: fast, certain, as-is close.`,
    inp.note ? `Additional context to weave in: ${inp.note}` : ``,
    ``,
    `Write the letter now. Remember: justify the number from the findings and project risk only — never reveal internal return math.`,
  ].filter(Boolean).join("\n");
}

export interface OfferLetterResult {
  text: string;
  negotiation: Negotiation;
}

/** Generate the letter. Cloud-only: reuses the `analyze` Edge Function (Claude). */
export async function generateOfferLetter(deal: Deal, inp: OfferLetterInputs, targetCap?: number): Promise<OfferLetterResult> {
  if (!isCloud()) throw new Error("AI letter generation needs cloud mode (the Anthropic key lives in the Edge Function).");
  const sb = getSupabase()!;
  const negotiation = buildNegotiation(deal, targetCap != null ? { targetCap } : undefined);
  const user = buildOfferLetterPrompt(deal, negotiation, inp);
  const { data, error } = await sb.functions.invoke("analyze", { body: { system: OFFER_LETTER_SYSTEM, user } });
  if (error) throw new Error(error.message || "letter generation failed");
  void logUsage("AI (Claude)", "offer letter");
  return { text: (data as { text: string }).text, negotiation };
}
